import React, { useEffect, useRef, useState } from "react";
import CompanyCard from "../components/CompanyCard";
import { Company } from "../schemas/auth";
import { companyKeyFor } from "../hooks/useAuthFlow";

interface ChainSearchProps {
  // null = no search run yet (show the prompt); [] = a real "no matches".
  results: Company[] | null;
  searchLoading: boolean;
  // ZC-5733: the 5 most recent chain selections, shown on the empty-query
  // landing state so the user can one-click back into a chain they use often.
  recents: Company[];
  onSearch: (query: string) => void;
  onSelect: (company: Company) => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

/**
 * ZC-5717: single cross-portal chain typeahead (support flow). Replaces the
 * portal chooser → per-portal chain picker two-step. The user types a query and
 * sees matching chains across all portals; each row shows its portal so
 * duplicates/ambiguity are distinguishable. Keyboard accessible: type to filter,
 * arrow keys to move, Enter to select.
 */
export default function ChainSearch({
  results,
  searchLoading,
  recents,
  onSearch,
  onSelect,
  onBack,
  loading,
  error,
}: ChainSearchProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the search so we don't fire a request per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // onSearch is stable (useCallback); intentionally keyed on query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Keep the active row in range as results change.
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);

  const shown = results ?? [];
  const trimmed = query.trim();
  const hasQuery = trimmed.length >= MIN_QUERY;

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (shown.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, shown.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const company = shown[activeIndex];
      if (company) onSelect(company);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="btn btn-link p-0 mb-4 text-muted d-flex align-items-center gap-1"
        aria-label="Go back"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h1 className="h3 fw-bold text-dark mb-1">Find a company</h1>
      <p className="text-muted mb-4">
        Search across all portals by name or code, then select a company to sign
        in to.
      </p>

      {error && (
        <div className="alert alert-danger py-2 mb-3" role="alert">
          {error}
        </div>
      )}

      <input
        type="text"
        className="form-control mb-2"
        placeholder="Search companies (e.g. Zen Global)"
        aria-label="Search companies across all portals"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
      />

      {searchLoading && (
        <p className="text-muted small mb-3" role="status" aria-live="polite">
          Searching…
        </p>
      )}

      {/* ZC-5733: empty-query landing — show Recent companies for a one-click
          re-pick, or the prompt when there are no recents yet. Once the user
          types a query, the live search results below take over. */}
      {!searchLoading && !hasQuery && recents.length > 0 && (
        <div>
          <p className="text-muted small mb-2">Recent companies</p>
          <div role="list" aria-label="Recent companies">
            {recents.map((company) => (
              <div key={companyKeyFor(company)} role="listitem">
                <CompanyCard
                  company={company}
                  onSelect={onSelect}
                  disabled={loading}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {!searchLoading && !hasQuery && recents.length === 0 && (
        <p className="text-muted">
          Start typing to search companies across all portals.
        </p>
      )}

      {!searchLoading && hasQuery && shown.length === 0 && (
        <p className="text-muted">No companies match your search.</p>
      )}

      {shown.length > 0 && (
        <div
          role="listbox"
          aria-label="Matching companies"
          className="overflow-auto"
          style={{ maxHeight: "60vh" }}
        >
          {shown.map((company, index) => (
            <div
              key={companyKeyFor(company)}
              role="option"
              aria-selected={index === activeIndex}
              className={
                index === activeIndex ? "chain-search__active" : undefined
              }
            >
              <CompanyCard
                company={company}
                onSelect={onSelect}
                disabled={loading}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
